import { getImageUrl } from '../admin/api/masterApi'

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || 'https://api.trivomtara.com/api'
).replace(/\/$/, '')

/** Shop products are assets in `services` with asset_type_id = 1 (Asset Master → Products). */
export const PRODUCT_ASSET_TYPE_ID = 1

const PROVIDER_VERIFY_FLAGS = [
  'education_verified',
  'criminal_record_verified',
  'business_integrity_verified',
  'kyc_verified',
  'bank_details_verified',
]

function parseGallery(value) {
  if (!value) return []
  if (Array.isArray(value)) return value.filter(Boolean)
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed.filter(Boolean) : []
    } catch {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    }
  }
  return []
}

function parseIdList(value) {
  if (Array.isArray(value)) {
    return value.map(Number).filter(Number.isFinite)
  }
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed)
      ? parsed.map(Number).filter(Number.isFinite)
      : []
  } catch {
    return String(value)
      .split(',')
      .map((item) => Number(item.trim()))
      .filter(Number.isFinite)
  }
}

function isActiveRecord(status) {
  if (status === undefined || status === null) return true
  if (typeof status === 'boolean') return status
  if (typeof status === 'string') {
    const normalized = status.trim().toLowerCase()
    return (
      normalized === 'active' ||
      normalized === 'yes' ||
      normalized === '1' ||
      normalized === 'true'
    )
  }
  return Number(status) === 1
}

function isProductAsset(service) {
  return Number(service?.asset_type_id) === PRODUCT_ASSET_TYPE_ID
}

function isSupplierOrServiceProvider(row) {
  const role = `${row.user_role_code || ''} ${row.user_role || ''}`
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
  return (
    role === 'supplier' ||
    role === 'suppliers' ||
    role === 'service_provider' ||
    role === 'service_providers' ||
    role.includes('supplier') ||
    role.includes('service_provider')
  )
}

function isProviderVerified(row) {
  return PROVIDER_VERIFY_FLAGS.every((flag) => Number(row[flag]) === 1)
}

function toNumber(value) {
  if (value == null || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

function stripTags(html) {
  if (!html) return ''
  return String(html)
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function decodeHtml(value) {
  if (!value) return ''
  return String(value)
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .trim()
}

/** Parse long HTML description into benefits list (same approach as mobile). */
export function parseProductDescription(html) {
  const raw = html || ''
  const strongs = [...raw.matchAll(/<strong>([^<]*)<\/strong>/gi)].map((m) =>
    decodeHtml(m[1].trim())
  )
  const benefits = [...raw.matchAll(/<li>([\s\S]*?)<\/li>/gi)].map((m) =>
    stripTags(m[1])
  )

  return {
    packInfo: strongs[0] || '',
    benefitsTitle: strongs[1] || (benefits.length ? 'Benefits' : ''),
    benefits: benefits.filter(Boolean),
  }
}

export function mapAssetProvider(row) {
  const name = [row.user_title, row.first_name, row.last_name]
    .filter(Boolean)
    .join(' ')

  return {
    id: String(row.user_id),
    name: name || 'Provider',
    role: row.user_role || '',
    experience: row.experience_years ? String(row.experience_years) : '',
    location: [row.location_name, row.state_name].filter(Boolean).join(', '),
    bio: row.brief_bio ? stripTags(row.brief_bio) : '',
    image: getImageUrl(row.profile_picture),
    linkedAssetIds: parseIdList(row.linked_asset_ids),
    isVerified: isProviderVerified(row),
  }
}

/** Map a services-table product asset into the shop UI shape. */
export function mapServiceToProduct(
  service,
  inventoryByServiceId = new Map(),
  providers = [],
  descriptionFallback = null
) {
  if (!service) return null

  const gallery = parseGallery(service.asset_picture_gallery)
  const images = [
    service.asset_profile_picture,
    ...gallery,
    service.service_icon,
  ].filter(Boolean)

  const uniqueImages = images.filter(
    (img, index, list) => list.indexOf(img) === index
  )

  const inventory = inventoryByServiceId.get(String(service.service_id))
  const basePrice =
    toNumber(service.price_inr) ??
    toNumber(service.price) ??
    toNumber(inventory?.unit_price_inr)
  const discountAmount =
    toNumber(service.discount_inr) ??
    toNumber(service.discount) ??
    toNumber(inventory?.discount_inr)
  const explicitSale = toNumber(service.discount_price)

  let price = basePrice
  let discountPrice = explicitSale

  if (
    discountPrice == null &&
    basePrice != null &&
    discountAmount != null &&
    discountAmount > 0
  ) {
    discountPrice = Math.max(0, basePrice - discountAmount)
  }

  const shortDescription =
    stripTags(service.short_description) ||
    stripTags(descriptionFallback?.short) ||
    ''
  const longRaw =
    service.long_description || descriptionFallback?.long || ''
  const parsed = parseProductDescription(longRaw)
  const longDescription = stripTags(longRaw)

  return {
    ...service,
    product_id: service.service_id,
    product_name: service.service_title,
    product_images: uniqueImages,
    product_description: shortDescription || longDescription || '',
    short_description: shortDescription,
    long_description: longDescription,
    benefits_title: parsed.benefitsTitle,
    benefits: parsed.benefits,
    pack_info: parsed.packInfo,
    providers,
    category: service.service_category_name || '',
    subCategory: service.service_sub_category_name || '',
    sort_order: toNumber(service.sort_order) ?? 0,
    price,
    discount_price: discountPrice,
    discount_percentage: toNumber(service.discount_percentage),
    quantity: toNumber(inventory?.quantity),
  }
}

/** Lower sort_order comes first (1 = first card). Missing/0 go last. */
export function compareProductSortOrder(a, b) {
  const orderA = toNumber(a?.sort_order)
  const orderB = toNumber(b?.sort_order)
  const rankA =
    orderA == null || orderA <= 0 ? Number.POSITIVE_INFINITY : orderA
  const rankB =
    orderB == null || orderB <= 0 ? Number.POSITIVE_INFINITY : orderB
  const idA = Number(a?.product_id ?? a?.service_id ?? 0)
  const idB = Number(b?.product_id ?? b?.service_id ?? 0)
  return rankA - rankB || idA - idB
}

async function fetchInventoryMap() {
  try {
    const res = await fetch(`${API_BASE_URL}/asset-inventory`)
    const data = await res.json()
    if (!res.ok || !data.success) return new Map()
    const map = new Map()
    for (const row of data.data || []) {
      const key = String(row.service_id ?? row.asset_id ?? '')
      if (key) map.set(key, row)
    }
    return map
  } catch (_error) {
    return new Map()
  }
}

async function fetchJson(path, fallbackMessage) {
  const res = await fetch(`${API_BASE_URL}${path}`)
  const data = await res.json()
  if (!res.ok || !data.success) {
    throw new Error(data.message || fallbackMessage)
  }
  return data.data
}

/** Providers/suppliers linked to the given asset id(s). */
export async function fetchAssetProviders(assetIds) {
  const idSet = new Set(
    (Array.isArray(assetIds) ? assetIds : [assetIds])
      .map(Number)
      .filter(Number.isFinite)
  )
  if (!idSet.size) return []

  try {
    const users = await fetchJson('/users', 'Failed to fetch providers')
    return (users || [])
      .filter((user) => {
        if (!isActiveRecord(user.status) || !isSupplierOrServiceProvider(user)) {
          return false
        }
        const linked = parseIdList(user.linked_asset_ids)
        return linked.some((id) => idSet.has(id))
      })
      .map(mapAssetProvider)
  } catch (_error) {
    return []
  }
}

export async function fetchProducts() {
  const [res, inventoryByServiceId] = await Promise.all([
    fetch(`${API_BASE_URL}/services`),
    fetchInventoryMap(),
  ])
  const data = await res.json()
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Failed to fetch products')
  }

  return (data.data || [])
    .filter(isProductAsset)
    .filter((row) => isActiveRecord(row.status))
    .map((row) => mapServiceToProduct(row, inventoryByServiceId))
    .sort(compareProductSortOrder)
}

export async function fetchProductById(id) {
  const [service, allServices, inventoryByServiceId] = await Promise.all([
    fetchJson(`/services/${id}`, 'Failed to fetch product'),
    fetchJson('/services', 'Failed to fetch products').catch(() => []),
    fetchInventoryMap(),
  ])

  if (!isProductAsset(service) || !isActiveRecord(service.status)) {
    throw new Error('Product not found')
  }

  const parentId =
    service.parent_asset_id != null
      ? Number(service.parent_asset_id)
      : Number(service.service_id)

  const familyAssetIds = [
    parentId,
    Number(service.service_id),
    ...(allServices || [])
      .filter(
        (item) =>
          isActiveRecord(item.status) &&
          isProductAsset(item) &&
          Number(item.parent_asset_id) === parentId
      )
      .map((item) => Number(item.service_id)),
  ]

  const parentRow =
    (allServices || []).find(
      (item) => Number(item.service_id) === parentId
    ) || null

  const providers = await fetchAssetProviders(familyAssetIds)

  return mapServiceToProduct(service, inventoryByServiceId, providers, {
    short: parentRow?.short_description,
    long: parentRow?.long_description,
  })
}

export function formatProductPrice(value) {
  if (value == null || value === '') return ''
  return Number(value).toLocaleString('en-IN')
}

export function getProductPrimaryImage(product) {
  const firstImage = product?.product_images?.[0]
  return getImageUrl(firstImage)
}

export { getImageUrl }
